import TypeOfGraphs from './TypeOfGraphs'
import HeaderOfGraphs from './HeaderOfGraphs'
import { AllData, BaseMap, CapaMapa, ChartData, Graphics, GraphicsDomain } from '../../../../../interfaces/Indicator'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useDownloadChart from '../../../../../hooks/useDownloadChart'
import { useMapLayersSelect } from '../../../hooks/useMapLayersSelect'
import exportToExcel from '../../../utils/exportToExcel'
import { handleDownloadMap } from '../../../utils/exportMapToPNG'
import { getChartTypeValue } from '../../../utils/helpers'
import { CHART_TYPES } from '../../../../../utils/chartTypes'
import { normalizeString } from '../../../../../utils/utils'
import { useSearchParams } from 'react-router-dom'
import { SEARCH_MODE_PREDICTIVE } from '../../../../../utils/const'
import { isMapGraph, matchesSelectedFilters } from '../Filters/helpers'

interface IContainerGraphs {
  graphics: Graphics[];
  source: string;
  name_indicator: string;
  breadcrumbs: string;
  data: AllData[];
  mapLayer: CapaMapa[];
}

const ContainerGraphs = ({ graphics, source, breadcrumbs, name_indicator, data, mapLayer }: IContainerGraphs) => {
  const [searchParams] = useSearchParams();
  const chartRef = useRef<HTMLDivElement>(null);
  const query_search = searchParams.get('search');

  const [chartIndex, setChartIndex] = useState<number>(0);
  const [chart, setChart] = useState<Graphics | null>(null);
  const [dataChart, setDataChart] = useState<ChartData[] | BaseMap[]>([]);
  const [disabledGoBack, setDisabledGoBack] = useState<boolean>(false);
  const [disabledGoForward, setDisabledGoForward] = useState<boolean>(graphics.length === 1);
  const [cleanFilters, setCleanFilters] = useState<boolean>(false);

  //Junta los datos de los filtros para descargarlo con el excel
  const [filtersSelected, setFiltersSelected] = useState<{ [key: string]: string[] }>({});

  // --> ESTADOS PARA MAPAS<-- //
  //Datos transformados para filtro de mapa
  const [dataMaps, setDataMaps] = useState<any[]>([]); //Guarda los datos base de los mapas
  const [scaleMap, setScaleMap] = useState<0 | 1 | null | undefined>(1); //1 asc | 0 desc
  // Capas activas + options/selectedItems 
  const mapLayersSelect = useMapLayersSelect(mapLayer, chart?.type_of_graph!, chartIndex);
  // --> FIN ESTADOS PARA MAPAS<-- //

  //Errores de información
  const [errorsNoData, setErrorsNoData] = useState<boolean>(false);

  //Información dinámica de mapas de calor (encabezado/anio/variable), filtrada según los filtros seleccionados actualmente
  const dynamicInfoFiltered = useMemo(() => {
    if (!chart?.dynamicInfo?.length) return [];
    return chart.dynamicInfo.filter((item) => matchesSelectedFilters(filtersSelected, item.encabezado, item.anio));
  }, [chart?.dynamicInfo, filtersSelected]);

  const TYPE_OF_INDICATOR: string = breadcrumbs.split('/').map(part => part.trim())[1] || '';
  const searchMode = searchParams.get('searchMode') ?? '';

  useEffect(() => {
    if (graphics.length === 0) return;

    if (query_search?.trim() && searchMode !== SEARCH_MODE_PREDICTIVE) {
      const matchedIndex = graphics.findIndex((graph) => normalizeString(graph?.titulo ?? '').includes(normalizeString(query_search ?? '')));
      setChartIndex(matchedIndex !== -1 ? matchedIndex : 0);
    } else {
      setChartIndex(0);
    }
  }, [graphics, query_search]);

  useEffect(() => {
    if (graphics.length === 0) return;

    const currentGraph = graphics[chartIndex];
    setChart(currentGraph);

    if (currentGraph.data_formated) {
      const filteredData = currentGraph.data_formated.filter((item: any) =>
        Object.values(item).some(value => value !== null)
      );
      setDataChart(filteredData);
    } else if (currentGraph?.maps) {
      setDataChart([]);
      setDataMaps(currentGraph.maps);
      setScaleMap(currentGraph.escala);
    }
  }, [chartIndex, graphics, TYPE_OF_INDICATOR]);

  useEffect(() => {
    setDisabledGoBack(chartIndex === 0);
    setDisabledGoForward(chartIndex === graphics.length - 1);
  }, [chartIndex, graphics.length]);

  // Sincroniza el año seleccionado en los filtros con el hook de capas
  useEffect(() => {
    if (!chart || !isMapGraph(chart.type_of_graph)) {
      mapLayersSelect.setYear(null);
      return;
    }

    const selectedYear = filtersSelected?.["Año"]?.[0] ?? null;
    mapLayersSelect.setYear(selectedYear);
  }, [filtersSelected, chart?.type_of_graph]);

  /* INICIO DESCARGA DE DATOS */
  const { downloadChart } = useDownloadChart(chartRef, chart!, source, TYPE_OF_INDICATOR, filtersSelected, breadcrumbs, name_indicator, dataChart as ChartData[], chart?.filtros ?? []);

  //Opciones para la descarga
  const optionsDropdownDowload = [
    {
      label: 'Imagen en .png', action: !chart?.maps
        ? () => downloadChart('png')
        : () => handleDownloadMap(chartRef, chart?.titulo!, filtersSelected, breadcrumbs, name_indicator, chart?.nota_grafico!, source, 'png', mapLayer, mapLayersSelect.activeLayersIds)
    },
    {
      label: 'Imagen en .jpg', action: !chart?.maps
        ? () => downloadChart('jpg')
        : () => handleDownloadMap(chartRef, chart?.titulo!, filtersSelected, breadcrumbs, name_indicator, chart?.nota_grafico!, source, 'jpg', mapLayer, mapLayersSelect.activeLayersIds)
    },
    { label: 'Datos en Excel', action: () => exportToExcel(dataChart!, source, chart?.titulo!, filtersSelected, chart?.nota_grafico!, breadcrumbs, name_indicator) },
  ];
  /* FIN DESCARGA DE DATOS */

  /* INICIO FUNCIONES DE MOVIMIENTO */
  const goBack = () => {
    if (chartIndex > 0) {
      setChartIndex(prevIndex => prevIndex - 1);
      setCleanFilters(true);
    }
  };

  const goForward = () => {
    if (chartIndex < graphics.length - 1) {
      setChartIndex(prevIndex => prevIndex + 1);
      setCleanFilters(true);
    }
  };
  /* FIN FUNCIONES DE MOVIMIENTO */

  /* START HANDLE CLEAN FILTERS */
  //Es usado para limpiar los filtros y reestablecer la información
  const handleCleanFilters = useCallback(() => {
    setCleanFilters(true);
  }, []);
  /* END HANDLE CLEAN FILTERS */

  if (!chart) {
    return null
  }

  return (
    <div className='shadow-custom-dual rounded-md'>
      <HeaderOfGraphs
        navigation={{ title: chart?.titulo ?? '', goBack, goForward, disabledGoBack, disabledGoForward }}
        filterProps={{
          typeOfIndicator: TYPE_OF_INDICATOR,
          data: !chart.maps?.length ? data : dataMaps,
          setDataChart,
          variable_analisis: chart?.variable_analisis ?? [],
          variable_analisis_axis_y_right: chart?.eje_y2 && chart.eje_y2_data?.variable_analisis ? chart.eje_y2_data.variable_analisis[0] : null,
          cleanFilters,
          setCleanFilters,
          setFiltersSelected,
          isPercentageChart: chart?.porcentual ?? null,
          handleCleanFilters,
          filters: chart?.filtros,
          typeOfGraph: chart?.type_of_graph,
          setErrorNoData: setErrorsNoData,
        }}
        optionsDropdownDowload={optionsDropdownDowload}
        mapLayers={mapLayersSelect}
      />
      <div className='pb-8'>
        <TypeOfGraphs
          chartId={chart.id}
          typeOfGraph={!chart.eje_y2 ? chart?.type_of_graph : CHART_TYPES.GRAFICO_DOBLE_EJE_Y}
          data={chart ? dataChart : []}
          chartRef={chartRef}
          typeOfIndicator={TYPE_OF_INDICATOR}
          variable_analisis={chart && chart.variable_analisis ? chart?.variable_analisis : []}
          domainAxisYLeft={{
            maximo: chart?.maximo ?? null,
            minimo: chart?.minimo ?? null,
            intervalo: chart?.intervalo ?? null,
            ticks: chart?.ticks ?? [],
          }}
          errorsNoData={errorsNoData}
          scale={scaleMap}
          {...(chart.eje_y2
            ? {
              chartTypeLeftId: getChartTypeValue(chart?.type_of_graph),
              variable_analisis_axis_y_right: chart.eje_y2_data?.variable_analisis?.[0] ?? null,
              domainAxisYRight: {
                maximo: chart.eje_y2_data?.maximo ?? null,
                minimo: chart.eje_y2_data?.minimo ?? null,
                intervalo: chart.eje_y2_data?.intervalo ?? null,
                ticks: chart.eje_y2_data?.ticks ?? [],
              } as GraphicsDomain,
            }
            : {})}
          filters={chart?.filtros}
          tooltip_indicadores_proc={chart.tooltip_indicadores_proc ?? []}
          mapLayer={mapLayersSelect.layersWithVisibleItems}
          activeLayersIds={mapLayersSelect.activeLayersIds}
          additionalInfoMaps={chart.additionalInfo}
          dynamicInfoMaps={dynamicInfoFiltered}
          orientation={chart.orientation ?? null}
        />
        {
          chart.nota_grafico &&
          <p className='px-8 pt-6 text-bsmall text-system-light-40'>Nota: {chart.nota_grafico}</p>
        }
      </div>
    </div>
  )
}

export default ContainerGraphs